// Ident draws a technical identifier as § conventions (density and length)
// states it: secondary to the plain name, in Geist Mono and the quiet colour,
// shortened from the middle to what fits — sha256:4f1c…a92e — with a copy
// button that copies the whole value. An identifier is never a heading and
// never a row's name; the caller puts the plain name first. Where the thing
// it names has a page, the text is a link to it (§ conventions: a name is a
// link; hq decision 0069), and NameLink is the same for a plain name.
import { Check, Copy } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";

/**
 * shorten keeps the start and the end of a long identifier and drops its
 * middle. A known prefix — a digest's algorithm, "sha256:" — is kept whole and
 * not counted, so every digest shortens to the same shape. A value short
 * enough to fit is returned unchanged.
 */
export function shorten(value: string, head = 6, tail = 4): string {
  const m = /^([a-z0-9]+:)(.*)$/.exec(value);
  const lead = m?.[1] ?? "";
  const prefix = lead.length <= 8 ? lead : "";
  const rest = value.slice(prefix.length);
  if (rest.length <= head + tail + 1) {
    return value;
  }
  return `${prefix}${rest.slice(0, head)}…${rest.slice(rest.length - tail)}`;
}

/**
 * Ident shows value shortened, with its whole value as the hover title and a
 * copy button. label names the identifier for the button — "digest", "id",
 * "subject" — so a screen reader hears "Copy digest".
 */
export function Ident({
  value,
  label = "value",
  head,
  tail,
  to,
}: {
  value: string;
  label?: string;
  head?: number;
  tail?: number;
  /** The page of the thing the identifier names, where it has one: the text becomes a link to it. */
  to?: string;
}) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) {
      return;
    }
    const t = setTimeout(() => {
      setCopied(false);
    }, 2000);
    return () => {
      clearTimeout(t);
    };
  }, [copied]);
  if (value === "") {
    return null;
  }
  return (
    <span className="ident">
      {to === undefined ? (
        <span className="mono" title={value}>
          {shorten(value, head, tail)}
        </span>
      ) : (
        // A link inside a table's row goes where it says, and never also opens the row.
        <Link
          to={to}
          className="mono ident-link"
          title={value}
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {shorten(value, head, tail)}
        </Link>
      )}
      <button
        type="button"
        className="ident-copy"
        aria-label={copied ? "Copied" : `Copy ${label}`}
        title={copied ? "Copied" : `Copy ${label}`}
        // A copy inside a table's row copies, and never opens the row.
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
          void navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
          });
        }}
      >
        {copied ? <Check size={12} strokeWidth={1.75} /> : <Copy size={12} strokeWidth={1.5} />}
      </button>
    </span>
  );
}

/**
 * NameLink is a thing's name as a link to its page (§ conventions: a name is a
 * link): the plain text, underlined as the console's text link, going where
 * it says and never also opening the row it sits in. A caller draws a name
 * as text, not a link, where the role is not offered the page, or on the
 * thing's own page.
 */
export function NameLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="text-link name-link"
      onKeyDown={(e) => {
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {children}
    </Link>
  );
}
